import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SaveOutlinedIcon from "@material-ui/icons/SaveOutlined";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import GetAppOutlinedIcon from "@material-ui/icons/GetAppOutlined";
import SearchIcon from "@material-ui/icons/Search";
import {
  AccountBalance,
  LocationOn,
  Map,
  Person,
  PieChart
} from "@material-ui/icons";
import LabeledTextField from "../../calc/shared/ui/HaklaiLabeledTextField";
import HelpDialog from "../../../layout/HelpDialog";
import CustomAcordion from "../../../layout/CustomAcordion";
import HaklaiAddressAutocomplete from "./HaklaiAddressAutocomplete";
import useHaklai2990Panel from "./useHaklai2990Panel";
import {
  HAKLAI_2990_ACTIONS,
  HAKLAI_2990_FIELD_LABELS,
  HAKLAI_2990_FIELD_TOOLTIPS,
  HAKLAI_2990_LISHKAT_RISHUM_OPTIONS,
  HAKLAI_2990_LABELS,
  HAKLAI_2990_MESSAGES,
  HAKLAI_2990_SMART_ADDRESS_HELP,
  getHaklai2990ParcelChoiceLabel
} from "./haklai2990Texts";

/**
 * Owns the editable Haklai 2990 result panel.
 *
 * Responsibilities:
 * - renders generated form fields from analysis data
 * - coordinates parcel lookup, save, and document download controls
 * - delegates side effects and payload building to `useHaklai2990Panel`
 */

const SMART_ADDRESS_HELP_DIALOG = (
  <Box textAlign="right">
    <Typography variant="body2" component="div" gutterBottom>
      {HAKLAI_2990_SMART_ADDRESS_HELP.formatsTitle}
    </Typography>
    <ul style={{ margin: "8px 0 0", paddingRight: "18px" }}>
      {HAKLAI_2990_SMART_ADDRESS_HELP.formats.map((format) => (
        <li key={format}>{format}</li>
      ))}
    </ul>
    <Typography variant="body2" component="div" style={{ marginTop: 12 }}>
      {HAKLAI_2990_SMART_ADDRESS_HELP.examplesTitle}
      {HAKLAI_2990_SMART_ADDRESS_HELP.examples.map((example) => (
        <React.Fragment key={example}>
          <br />
          {example}
        </React.Fragment>
      ))}
    </Typography>
  </Box>
);

const FORM_FIELD_INPUT_WIDTH = 168;
const FRACTION_INPUT_WIDTH = 72;
const SELLER_FIELDS = [
  { type: "text", label: HAKLAI_2990_FIELD_LABELS.sellerFirstName, name: "sellerFirstName", maxLength: "17" },
  { type: "text", label: HAKLAI_2990_FIELD_LABELS.sellerLastName, name: "sellerLastName", maxLength: "17" },
  {
    type: "number",
    label: HAKLAI_2990_FIELD_LABELS.sellerId,
    name: "sellerId",
    noComma: true,
    sequencesOfZerosAllowed: true,
    maxLength: "9",
    tooltip: HAKLAI_2990_FIELD_TOOLTIPS.sellerId
  }
];
const OFFICE_FIELDS = [
  {
    type: "select",
    label: HAKLAI_2990_LABELS.taxOfficePlace,
    name: "lishcatRishumLocation",
    useOfficeOptions: true
  }
];
const PROPERTY_ID_FIELDS = [
  { type: "number", label: HAKLAI_2990_FIELD_LABELS.blockNum, name: "blockNum", noComma: true, maxLength: "6" },
  { type: "number", label: HAKLAI_2990_FIELD_LABELS.subBlock, name: "subBlock", noComma: true, maxLength: "4" },
  { type: "number", label: HAKLAI_2990_FIELD_LABELS.subSubBlock, name: "subSubBlock", noComma: true, maxLength: "3" }
];
const ADDRESS_PRIMARY_FIELDS = [
  { type: "text", label: HAKLAI_2990_FIELD_LABELS.assetCity, name: "assetCity", addressAutocomplete: true },
  { type: "text", label: HAKLAI_2990_FIELD_LABELS.assetStreet, name: "assetStreet", addressAutocomplete: true },
  { type: "number", label: HAKLAI_2990_FIELD_LABELS.assetStreetNumber, name: "assetStreetNumber", noComma: true, maxLength: "5" }
];
const ADDRESS_SECONDARY_FIELDS = [
  { type: "number", label: HAKLAI_2990_FIELD_LABELS.assetHouseNum, name: "assetHouseNum", noComma: true, maxLength: "4" },
  { type: "number", label: HAKLAI_2990_FIELD_LABELS.assetZipcode, name: "assetZipcode", noComma: true, maxLength: "7" }
];

const useStyles = makeStyles((theme) => ({
  lookupInfoBox: {
    color: "#1f7a4d",
    display: "block",
    fontWeight: 600,
    lineHeight: 1.45
  },
  lookupError: {
    color: theme.palette.error.main,
    fontWeight: 600
  },
  jsonErrorField: {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: `${theme.palette.error.main} !important`
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: `${theme.palette.error.main} !important`
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: `${theme.palette.error.main} !important`
    },
    "& .MuiFormHelperText-root": {
      color: theme.palette.error.main
    }
  },
  jsonEmptyState: {
    color: "#7a8fa3",
    marginTop: theme.spacing(1.25)
  }
}));

const HaklaiResults2990Panel = ({
  dealId,
  haklaiDraftPreCalc,
  analyzedPreCalc,
  fullName,
  licenseNumber,
  disableTaxAuthorityDocumentConfirmation
}) => {
  const classes = useStyles();
  const {
    activeJsonButton,
    addressChoices,
    addresses,
    addressMenuAnchor,
    addressSearchLoading,
    closeAddressParcelMenu,
    docFields,
    exportGroups,
    exportGroupsError,
    handleAddressChoiceClick,
    handleAddressKeyPress,
    handleAddressSearchClick,
    handleDocFieldChange,
    handleGenerateDeductions,
    handleGenerateJson,
    handleGeneratePdf,
    handleParcelSearchFieldChange,
    handleSave,
    hasGovMapAutofill,
    isDocumentActionBusy,
    isGeneratingDeductions,
    isGeneratingPdf,
    isLoading,
    isSaving,
    jsonFieldErrors,
    jsonValidationFocusRequest,
    loadError,
    parcelAddressError,
    parcelAddressSearch,
    parcelMenuAnchorRef,
    parcelPairError
  } = useHaklai2990Panel({
    analyzedPreCalc,
    dealId,
    haklaiDraftPreCalc,
    taxAuthorityAccountIdentity: {
      fullName,
      licenseNumber,
      disableTaxAuthorityDocumentConfirmation
    }
  });

  const fieldRefs = useRef({});
  const registerFieldRef = (fieldName) => (node) => {
    if (node) fieldRefs.current[fieldName] = node;
    else delete fieldRefs.current[fieldName];
  };

  useEffect(() => {
    const fieldName = jsonValidationFocusRequest?.fieldName;
    if (!fieldName) return;

    const fieldElement = fieldRefs.current[fieldName];
    if (!fieldElement) return;

    if (typeof fieldElement.scrollIntoView === "function") {
      fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    const focusTarget = fieldElement.querySelector("input, textarea, button, [tabindex]");
    if (focusTarget && typeof focusTarget.focus === "function") {
      try {
        focusTarget.focus({ preventScroll: true });
      } catch {
        focusTarget.focus();
      }
    }
  }, [jsonValidationFocusRequest]);

  const officeOptions = HAKLAI_2990_LISHKAT_RISHUM_OPTIONS;
  const fieldsDisabled = isLoading || isDocumentActionBusy;
  const renderFieldInput = (field) => {
    const {
      label,
      tooltip,
      useOfficeOptions,
      addressAutocomplete,
      ...textFieldProps
    } = field;
    const inputNode = addressAutocomplete ? (
      <HaklaiAddressAutocomplete
        addresses={addresses}
        city={docFields.assetCity}
        disabled={fieldsDisabled || textFieldProps.disabled}
        error={Boolean(jsonFieldErrors[field.name]) || textFieldProps.error}
        helperText={jsonFieldErrors[field.name] || textFieldProps.helperText}
        label={label}
        name={field.name}
        onChange={handleDocFieldChange}
        textFieldWidth={FORM_FIELD_INPUT_WIDTH}
        value={docFields[field.name]}
      />
    ) : (
      <LabeledTextField
        {...textFieldProps}
        options={useOfficeOptions ? officeOptions : field.options}
        value={docFields[field.name]}
        textFieldWidth={FORM_FIELD_INPUT_WIDTH}
        labelSpacing={0}
        inputSpacing={0}
        disabled={fieldsDisabled || textFieldProps.disabled}
        error={Boolean(jsonFieldErrors[field.name]) || textFieldProps.error}
        helperText={jsonFieldErrors[field.name] || textFieldProps.helperText || undefined}
        onChange={handleDocFieldChange}
      />
    );
    const wrappedInput = (
      <div
        className={jsonFieldErrors[field.name] ? classes.jsonErrorField : ""}
        ref={registerFieldRef(field.name)}
      >
        {inputNode}
      </div>
    );

    if (!tooltip) return wrappedInput;

    return (
      <Tooltip title={tooltip} placement="bottom-start">
        <div>{wrappedInput}</div>
      </Tooltip>
    );
  };
  const renderFormFieldTable = (fields) => (
    <table className="deal-sum-field-table">
      <tbody>
        {fields.map((field) => (
          <tr key={field.name}>
            <th>{field.label}</th>
            <td>{renderFieldInput(field)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <>
      <CustomAcordion summaryText={HAKLAI_2990_LABELS.panelTitle} defaultExpanded={true}>
        <div className="deal-sum-form">
          <section className="deal-sum-fact-section haklai-results-lookup">
            <h3 className="deal-sum-fact-section-title">
              <span className="deal-sum-section-icon" aria-hidden="true">
                <LocationOn />
              </span>
              {HAKLAI_2990_LABELS.propertyLookup}
              <HelpDialog
                label={HAKLAI_2990_LABELS.propertyLookup}
                helpDialog={SMART_ADDRESS_HELP_DIALOG}
              />
            </h3>
            <div className="haklai-results-lookup-bar" ref={parcelMenuAnchorRef}>
              <TextField
                className="haklai-results-lookup-input"
                disabled={fieldsDisabled}
                name="parcelAddressSearch"
                onChange={handleParcelSearchFieldChange}
                onKeyPress={handleAddressKeyPress}
                placeholder={HAKLAI_2990_SMART_ADDRESS_HELP.placeholder}
                size="small"
                value={parcelAddressSearch}
                variant="outlined"
              />
              <Button
                className="deal-sum-primary-btn haklai-results-lookup-search"
                disabled={fieldsDisabled || addressSearchLoading || !String(parcelAddressSearch || "").trim()}
                onClick={handleAddressSearchClick}
                endIcon={addressSearchLoading ? <CircularProgress color="inherit" size={16} /> : <SearchIcon />}
              >
                {HAKLAI_2990_ACTIONS.searchPropertyDetails}
              </Button>
            </div>
            {parcelAddressError ? (
              <Typography variant="caption" component="div" className={classes.lookupError}>
                {parcelAddressError}
              </Typography>
            ) : hasGovMapAutofill ? (
              <Typography variant="caption" component="div" className={classes.lookupInfoBox}>
                {HAKLAI_2990_MESSAGES.smartAddressAutofill}
              </Typography>
            ) : null}
            {loadError ? (
              <Typography variant="body2" className={classes.lookupError}>
                {loadError}
              </Typography>
            ) : null}
            <Menu
              anchorEl={addressMenuAnchor}
              open={Boolean(addressMenuAnchor) && addressChoices.length > 1}
              onClose={closeAddressParcelMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              getContentAnchorEl={null}
            >
              {addressChoices.map((pair) => (
                <MenuItem
                  key={`${pair.gush}-${pair.helka}-${pair.objectId != null ? pair.objectId : ""}`}
                  onClick={() => handleAddressChoiceClick(pair)}
                >
                  {getHaklai2990ParcelChoiceLabel(pair)}
                </MenuItem>
              ))}
            </Menu>
          </section>

          <div className="deal-sum-form-row">
            <section className="deal-sum-fact-section">
              <h3 className="deal-sum-fact-section-title">
                <span className="deal-sum-section-icon" aria-hidden="true">
                  <Person />
                </span>
                {HAKLAI_2990_LABELS.sellerDetails}
              </h3>
              {renderFormFieldTable(SELLER_FIELDS)}
            </section>
            <section className="deal-sum-fact-section">
              <h3 className="deal-sum-fact-section-title">
                <span className="deal-sum-section-icon" aria-hidden="true">
                  <AccountBalance />
                </span>
                {HAKLAI_2990_LABELS.taxOffice}
              </h3>
              {renderFormFieldTable(OFFICE_FIELDS)}
            </section>
          </div>

          <div className="deal-sum-form-row">
            <section className="deal-sum-fact-section">
              <h3 className="deal-sum-fact-section-title">
                <span className="deal-sum-section-icon" aria-hidden="true">
                  <Map />
                </span>
                {HAKLAI_2990_LABELS.propertyIdentification}
              </h3>
              {renderFormFieldTable(PROPERTY_ID_FIELDS)}
              {parcelPairError ? (
                <Typography variant="body2" className={classes.lookupError}>
                  {parcelPairError}
                </Typography>
              ) : null}
            </section>
            <section className="deal-sum-fact-section">
              <h3 className="deal-sum-fact-section-title">
                <span className="deal-sum-section-icon" aria-hidden="true">
                  <PieChart />
                </span>
                {HAKLAI_2990_LABELS.transferredShare}
              </h3>
              <div className="deal-sum-fraction-wrap">
                <div className="deal-sum-fraction">
                  <div
                    className={jsonFieldErrors.passPartMekarkeinMechane ? classes.jsonErrorField : ""}
                    ref={registerFieldRef("passPartMekarkeinMechane")}
                  >
                    <LabeledTextField
                      type="number"
                      noComma
                      maxLength="12"
                      textFieldWidth={FRACTION_INPUT_WIDTH}
                      value={docFields.passPartMekarkeinMechane}
                      name="passPartMekarkeinMechane"
                      label={HAKLAI_2990_LABELS.soldShareDenominator}
                      disabled={fieldsDisabled}
                      error={Boolean(jsonFieldErrors.passPartMekarkeinMechane)}
                      helperText={jsonFieldErrors.passPartMekarkeinMechane || undefined}
                      onChange={handleDocFieldChange}
                    />
                  </div>
                  <div className="deal-sum-fraction-slash">/</div>
                  <div
                    className={jsonFieldErrors.passPartMekarkeinMone ? classes.jsonErrorField : ""}
                    ref={registerFieldRef("passPartMekarkeinMone")}
                  >
                    <LabeledTextField
                      type="number"
                      noComma
                      maxLength="12"
                      textFieldWidth={FRACTION_INPUT_WIDTH}
                      value={docFields.passPartMekarkeinMone}
                      name="passPartMekarkeinMone"
                      label={HAKLAI_2990_LABELS.soldShareNumerator}
                      disabled={fieldsDisabled}
                      error={Boolean(jsonFieldErrors.passPartMekarkeinMone)}
                      helperText={jsonFieldErrors.passPartMekarkeinMone || undefined}
                      onChange={handleDocFieldChange}
                    />
                  </div>
                </div>
                <div className="deal-sum-fraction-hint">{HAKLAI_2990_LABELS.soldShareHint}</div>
              </div>
            </section>
          </div>

          <div className="deal-sum-form-row is-wide">
            <section className="deal-sum-fact-section">
              <h3 className="deal-sum-fact-section-title">
                <span className="deal-sum-section-icon" aria-hidden="true">
                  <LocationOn />
                </span>
                {HAKLAI_2990_LABELS.propertyAddress}
              </h3>
              <div className="deal-sum-address-grid">
                {renderFormFieldTable(ADDRESS_PRIMARY_FIELDS)}
                {renderFormFieldTable(ADDRESS_SECONDARY_FIELDS)}
              </div>
            </section>
          </div>

          <div className="deal-sum-form-actions">
            <Button
              className="pre-calc-results-expand"
              endIcon={isSaving ? <CircularProgress color="inherit" size={18} /> : <SaveOutlinedIcon />}
              disabled={fieldsDisabled}
              onClick={handleSave}
            >
              {HAKLAI_2990_ACTIONS.savePersonalDetails}
            </Button>
          </div>
        </div>
      </CustomAcordion>

      <CustomAcordion summaryText={HAKLAI_2990_LABELS.documentsTitle} defaultExpanded={true}>
        <div className="deal-sum-docs">
          <div className="deal-sum-docs-block">
            <Typography variant="subtitle1" align="right">
              {HAKLAI_2990_LABELS.additionalActions}
            </Typography>
            <div className="deal-sum-action-buttons">
              <Button
                className="deal-sum-admin-btn"
                endIcon={isGeneratingPdf ? <CircularProgress color="inherit" size={18} /> : <DescriptionOutlinedIcon />}
                disabled={fieldsDisabled || Boolean(exportGroupsError)}
                onClick={handleGeneratePdf}
              >
                {HAKLAI_2990_ACTIONS.generateForm2990}
              </Button>
              <Button
                className="deal-sum-admin-btn"
                endIcon={isGeneratingDeductions ? <CircularProgress color="inherit" size={18} /> : <DescriptionOutlinedIcon />}
                disabled={fieldsDisabled || Boolean(exportGroupsError)}
                onClick={handleGenerateDeductions}
              >
                {HAKLAI_2990_ACTIONS.generateDeductions}
              </Button>
            </div>
          </div>

          <div className="deal-sum-docs-block">
            <Typography variant="subtitle1" align="right">
              {HAKLAI_2990_LABELS.onlineFiles}
            </Typography>

            {exportGroupsError || exportGroups.length === 0 ? (
              <Typography variant="body2" className={classes.jsonEmptyState}>
                {exportGroupsError || HAKLAI_2990_MESSAGES.exportFilesEmpty}
              </Typography>
            ) : null}

            <div className="deal-sum-file-buttons">
              {exportGroups.map((group) => {
                const groupIdentity = group.haklaiPropertyKey;
                return (
                  <Box key={`json-group-${groupIdentity}`} className="deal-sum-file-group">
                    <div className="deal-sum-file-group-title">
                      {group.title}
                    </div>
                    <div className="deal-sum-file-buttons">
                      {group.needsMutav ? (
                        <Button
                          className="deal-sum-primary-btn"
                          endIcon={
                            activeJsonButton === `mutav-${groupIdentity}`
                              ? <CircularProgress color="inherit" size={16} />
                              : <GetAppOutlinedIcon />
                          }
                          disabled={fieldsDisabled}
                          onClick={() => handleGenerateJson("mutav", groupIdentity)}
                        >
                          {HAKLAI_2990_ACTIONS.generateMutavFile}
                        </Button>
                      ) : null}
                      {group.needsRegTax ? (
                        <Button
                          className="deal-sum-primary-btn"
                          endIcon={
                            activeJsonButton === `linear-${groupIdentity}`
                              ? <CircularProgress color="inherit" size={16} />
                              : <GetAppOutlinedIcon />
                          }
                          disabled={fieldsDisabled}
                          onClick={() => handleGenerateJson("linear", groupIdentity)}
                        >
                          {HAKLAI_2990_ACTIONS.generateLinearFile}
                        </Button>
                      ) : null}
                      {!group.needsMutav && !group.needsRegTax ? (
                        <Typography variant="body2" className={classes.jsonEmptyState}>
                          {HAKLAI_2990_MESSAGES.noExportRoutesForPart}
                        </Typography>
                      ) : null}
                    </div>
                  </Box>
                );
              })}
            </div>
          </div>
        </div>
      </CustomAcordion>
    </>
  );
};

const mapStateToProps = (state) => ({
  fullName: state.loggedinReducer.fullName,
  licenseNumber: state.loggedinReducer.licenseNumber,
  disableTaxAuthorityDocumentConfirmation: state.loggedinReducer.disableTaxAuthorityDocumentConfirmation
});

export default connect(mapStateToProps)(React.memo(HaklaiResults2990Panel));
